import { useEffect, useState } from "react";
import { Users, PawPrint, CalendarCheck, DollarSign, Syringe, Bell, Truck } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { Spinner } from "../components/ui/Spinner";
import { PageHeader } from "../components/ui/PageHeader";
import { Badge } from "../components/ui/Badge";
import { EmptyState } from "../components/ui/EmptyState";
import { useAuth } from "../context/AuthContext";

interface ResumenDashboard {
  totalClientes: number;
  totalMascotas: number;
  citasHoy: any[];
  recordatoriosHoy: any[];
  domicilioHoy: any[];
  totalVentasMes: number;
  numeroVentasMes: number;
  productosStockBajo: any[];
  vacunasProximas: any[];
}

// `borde` es el acento de la barra superior de la tarjeta (a tono con el
// resto del portal: menú lateral navy + morado) y `demora` escalona la
// entrada de cada tarjeta unas décimas de segundo entre una y otra, para
// que no aparezcan todas de golpe.
function tarjeta(icono: React.ReactNode, etiqueta: string, valor: string | number, color: string, borde: string, demora = 0) {
  return (
    <div
      className={`card entrada-contenido flex items-center gap-4 border-t-4 p-5 ${borde}`}
      style={{ animationDelay: `${demora}ms`, animationFillMode: "backwards" }}
    >
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>{icono}</div>
      <div>
        <p className="text-xs text-slate-400">{etiqueta}</p>
        <p className="text-xl font-semibold text-slate-800">{valor}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { usuario } = useAuth();
  const [datos, setDatos] = useState<ResumenDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get("/reportes/dashboard")
      .then((res) => setDatos(res.data))
      .catch((err) => setError(mensajeError(err)));
  }, []);

  return (
    <div>
      <PageHeader titulo={`Hola, ${usuario?.nombre?.split(" ")[0] ?? ""} 👋`} descripcion="Este es el resumen de tu negocio hoy." icono={<PawPrint size={20} />} />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {!datos ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {tarjeta(<Users size={20} className="text-blue-600" />, "Clientes activos", datos.totalClientes, "bg-blue-50", "border-t-blue-400", 0)}
            {tarjeta(<PawPrint size={20} className="text-violet-600" />, "Mascotas registradas", datos.totalMascotas, "bg-violet-50", "border-t-violet-400", 60)}
            {tarjeta(<CalendarCheck size={20} className="text-sky-600" />, "Citas de hoy", datos.citasHoy.length, "bg-sky-50", "border-t-sky-400", 120)}
            {tarjeta(
              <DollarSign size={20} className="text-emerald-600" />,
              "Ventas del mes",
              `$${datos.totalVentasMes.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
              "bg-emerald-50",
              "border-t-emerald-400",
              180
            )}
          </div>

          <div>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Actividades programadas para hoy</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="card border-l-4 border-l-sky-400 p-5">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <CalendarCheck size={16} className="text-sky-500" /> Citas
                </h3>
                {datos.citasHoy.length === 0 ? (
                  <EmptyState compacto titulo="Sin citas hoy" descripcion="No hay nada agendado por ahora." />
                ) : (
                  <ul className="space-y-3">
                    {datos.citasHoy.map((c: any) => (
                      <li key={c.id} className="flex items-center justify-between text-sm">
                        <div>
                          <p className="font-medium text-slate-700">
                            {c.mascota?.nombre ?? "—"} · {c.cliente?.nombre}
                          </p>
                          <p className="text-xs text-slate-400">{c.tipoServicio}</p>
                        </div>
                        <Badge color="azul">{new Date(c.fechaInicio).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="card border-l-4 border-l-amber-400 p-5">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Bell size={16} className="text-amber-500" /> Recordatorios
                </h3>
                {datos.recordatoriosHoy.length === 0 ? (
                  <EmptyState compacto titulo="Sin recordatorios hoy" descripcion="No hay nada programado por ahora." />
                ) : (
                  <ul className="space-y-3">
                    {datos.recordatoriosHoy.map((r: any) => (
                      <li key={r.id} className="flex items-center justify-between text-sm">
                        <div>
                          <p className="font-medium text-slate-700">
                            {r.cliente?.nombre}
                            {r.mascota?.nombre ? ` · ${r.mascota.nombre}` : ""}
                          </p>
                          <p className="text-xs text-slate-400">{r.canal} · {r.tipo}</p>
                        </div>
                        <Badge color={r.enviado ? "verde" : "amarillo"}>
                          {new Date(r.programadoPara).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="card border-l-4 border-l-violet-400 p-5">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Truck size={16} className="text-violet-500" /> A domicilio
                </h3>
                {datos.domicilioHoy.length === 0 ? (
                  <EmptyState compacto titulo="Sin servicios a domicilio hoy" descripcion="No hay nada agendado por ahora." />
                ) : (
                  <ul className="space-y-3">
                    {datos.domicilioHoy.map((s: any) => (
                      <li key={s.id} className="flex items-center justify-between text-sm">
                        <div>
                          <p className="font-medium text-slate-700">
                            {s.mascota?.nombre ?? "—"} · {s.cliente?.nombre}
                          </p>
                          <p className="text-xs text-slate-400 truncate max-w-[160px]">{s.direccion}</p>
                        </div>
                        <Badge color="azul">{new Date(s.fecha).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="card border-l-4 border-l-violet-400 p-5">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Syringe size={16} className="text-violet-500" /> Próximas vacunas
            </h3>
            {datos.vacunasProximas.length === 0 ? (
              <EmptyState compacto titulo="Sin vacunas próximas" descripcion="Nada por vencer en los próximos días." />
            ) : (
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {datos.vacunasProximas.map((v: any) => (
                  <li key={v.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                    <span className="text-slate-700">
                      {v.mascota?.nombre} · {v.nombre}
                    </span>
                    <span className="text-xs text-slate-400">{new Date(v.fechaProximaDosis).toLocaleDateString("es-MX")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
